/**
 * Distribution Strategy Manager - Implements the mature strategy pattern from prototype
 *
 * This module provides configurable distribution strategies following the engine-prototype's
 * proven architecture:
 * - Strategy pattern with clean interfaces
 * - Automatic strategy determination based on bond configuration
 * - Shared utilities for common calculations
 * - Configuration-driven behavior
 *
 * Replaces the hardcoded distribution logic in revenue-distributor.ts lines 277-305
 */

import type { RevenueMap } from '~/types';
// Config is now simplified - see config.ts for hardcoded values
import { UnifiedDistributionStrategy } from './strategies';
import type { RevenueDistributionHelperMap } from './types';

/**
 * Distribution Strategy Manager - Main orchestrator for distribution strategies
 * Implements the mature strategy pattern from the engine-prototype
 */
export class DistributionStrategyManager {
  private static unifiedStrategy = new UnifiedDistributionStrategy();

  /**
   * Executes the unified distribution strategy
   * Main entry point for revenue distribution calculations
   */
  static executeStrategy(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailableForDistribution: number
  ): void {
    DistributionStrategyManager.unifiedStrategy.distribute(
      helperMap,
      revenueMap,
      currentTaxYear,
      revenueAvailableForDistribution
    );
  }

  /**
   * Convenience method that combines strategy determination and execution
   * Recommended for most use cases
   */
  static distributeRevenue(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailableForDistribution: number
  ): void {
    // Always use unified strategy - no need for strategy determination
    DistributionStrategyManager.executeStrategy(
      helperMap,
      revenueMap,
      currentTaxYear,
      revenueAvailableForDistribution
    );
  }

  // Configuration is now hardcoded - see config.ts and smart-ratio-calculator.ts
  // for the actual values being used
}
